import { useCallback, useState } from "react";
import { searchApi } from "@services/searchApi";
import type { SearchResponse, TopicTreeCapability } from "@models/search";

export interface TopicTreeSearchState {
  loading: boolean;
  error: string | null;
  result: SearchResponse | null;
  run: (capability: TopicTreeCapability, query: string, topK: number) => Promise<void>;
  reset: () => void;
}

/** Free-text search over one topic-tree index (technical manual or wiring diagram). */
export function useTopicTreeSearch(): TopicTreeSearchState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SearchResponse | null>(null);

  const run = useCallback(async (capability: TopicTreeCapability, query: string, topK: number) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await searchApi.search(capability, { query, topK }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setResult(null);
  }, []);

  return { loading, error, result, run, reset };
}
